<template>
  <v-dialog v-model="show" width="85%" persistent>
    <v-card id="modalContainerCard">
      <v-icon class="modal-close" @click="closeModal">
        close
      </v-icon>
      <v-card-title class="modal-title">
        Update Info
      </v-card-title>
      <v-layout wrap>
        <v-flex xs5 md3 class="pt-3 pb-4">
          <div class="container-input text-xs-left">
            <label class="area-label">Add More {{ preferenceType }}s</label>
            <input
              v-model="typedPreference"
              :class="{'pref-error' : prefError}"
              type="text"
              @input="prefError = false"
              @keyup.enter="addTypedPreference()"
            >
          </div>
          <div
            v-if="prefError"
            class="pref-error-text"
          >
            Preference already exists as need or want
          </div>
        </v-flex>
        <v-flex xs3 md3 class="pt-4">
          <v-btn
            :color="colors.hwayzBrandBlue"
            ripple
            outline
            small
            class="add-button"
            @click.stop="addTypedPreference()"
          >
            Add
          </v-btn>
        </v-flex>
        <v-flex v-if="showUpdateButton" md4 offset-md1 class="text-xs-right pt-4 pb-2 update-needs-wants-btn">
          <loader-button
            :loading="processUpdate"
            :loading-message="'Updating feedbacks...'"
            btn-class="white--text px-5 update-needs-wants-btn"
            :btn-settings="{color: colors.hwayzBrandBlue}"
            :btn-text="`Update ${preferenceType}s`"
            @handle-button-click="updateProfileWantNeed()"
          />
        </v-flex>
      </v-layout>
      <div class="divider">
        <div class="preference-container">
          <p>Selected: </p>
          <v-chip
            v-for="(item, i) in preferenceArray"
            :key="i"
            class="chips"
            small
            :color="getPreferenceColor"
            @click="preferenceToSuggestion(item)"
          >
            {{ item }}
          </v-chip>
        </div>
        <div class="preference-container">
          <p>Options: </p>
          <v-chip
            v-for="(item, i) in suggestions"
            :key="i"
            class="chips"
            small
            :color="colors.hwayzDarkGray"
            @click="suggestionToPreference(item)"
          >
            {{ item }}
          </v-chip>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import { genericGetRequestToApi, genericPutRequestToApi } from '../../utils/genericRequests'
import { RetrieveProfileById, UpdateFeedbackMatchUrl } from '../../utils/clientUrls'
import { isEmpty } from '../../utils/generalUtils'
import LoaderButton from '../uiUtils/LoaderButton.vue'
import refreshContent from '../mixins/refreshContent'
import colors from '../../scss/_colors.scss'
import refreshClientData from '../mixins/refreshClientData'

export default {
  components: { LoaderButton },
  mixins: [refreshContent, refreshClientData],
  props: {
    show: {
      type: Boolean,
      default: false
    },
    buttonText: {
      type: String,
      default: 'OK'
    },
    preferenceType: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      colors,
      processUpdate: false,
      showUpdateButton: false,
      typedPreference: '',
      preferenceArray: [],
      suggestions: [],
      originalNeedWants: [],
      prefError: false
    }
  },
  computed: {
    ...mapState('style', ['modal']),
    ...mapGetters('client', ['getInitialPrefSuggestions', 'feedbackProvidedHomes']),

    getPreferenceColor() {
      return this.preferenceType === 'Want' ? colors.hwayzDarkenGreen : colors.hwayzTapestry
    }
  },

  watch: {
    clientData: {
      handler: function(newValue, oldValue) {
        this.preferenceArray = this.getClientHomePreferences(newValue, this.preferenceType)
        if (newValue.homePrefs) this.originalNeedWants = newValue.homePrefs
      },
      deep: true
    }
  },
  created() {
    this.preferenceArray = this.getClientHomePreferences(this.clientData, this.preferenceType)

    genericGetRequestToApi(RetrieveProfileById(this.clientData._id)).then(
      response => {
        if (response.data.homePrefs) this.originalNeedWants = response.data.homePrefs
      }
    )
  },
  methods: {
    ...mapMutations('client', {
      'setClientData': 'SET_CLIENT_DATA'
    }),

    async updateProfileWantNeed() {
      let otherPreferences = []
      if (this.originalNeedWants.options) {
        otherPreferences = this.originalNeedWants.options
          .filter(needWants => needWants.wantNeed !== this.preferenceType)
          .map(preference => {
            return { text: preference.text, wantNeed: this.preferenceType === 'Want' ? 'Need' : 'Want' }
          })
      }

      const preferencesToAdd = this.preferenceArray.map(preference => {
        return { text: preference, wantNeed: this.preferenceType }
      })

      const payloadHomePrefs = {
        freetext: '',
        options: [...otherPreferences, ...preferencesToAdd]
      }

      const response = await genericPutRequestToApi(RetrieveProfileById(this.clientData._id), {
        homePrefs: payloadHomePrefs
      })
      if (response.status === 200) {
        const updatedClientData = { ...this.clientData, ...{ homePrefs: payloadHomePrefs } }
        this.setClientData(updatedClientData)
        this.processUpdate = true
        this.updateFeedbackData(payloadHomePrefs.options)
      }
    },

    addTypedPreference() {
      let allClientPrefs = []
      if (this.clientData && this.clientData.homePrefs) {
        allClientPrefs = [...this.clientData.homePrefs.options]
      }
      const isClientPref = allClientPrefs.some(
        pref => pref.text.toUpperCase().trim() === this.typedPreference.toUpperCase().trim()
      )
      const isEntryPresent = this.preferenceArray.some(
        pref => pref.toUpperCase().trim() === this.typedPreference.toUpperCase().trim()
      )
      if (!isEmpty(this.typedPreference)) {
        if (!isEntryPresent && !isClientPref) {
          this.showUpdateButton = true
          this.preferenceArray.push(this.typedPreference)
          this.typedPreference = ''
        } else {
          this.prefError = true
        }
      }
    },

    suggestionToPreference(sug) {
      this.preferenceArray.push(sug)
      this.suggestions = this.suggestions.filter(
        suggestion => suggestion !== sug
      )
      this.showUpdateButton = true
    },

    preferenceToSuggestion(preference) {
      this.suggestions.push(preference)
      this.preferenceArray = this.preferenceArray.filter(pref => pref !== preference)
      this.showUpdateButton = true
    },

    getClientHomePreferences(client, preference) {
      this.suggestions = this.getInitialPrefSuggestions.map(preference => preference.text)
      if (client && client.homePrefs) {
        return client.homePrefs.options
          .filter(pref => pref.wantNeed === preference)
          .map(item => {
            if (item.minValue) {
              return item.text + '-' + item.minValue
            }
            return item.text
          })
      }
      return []
    },

    async updateFeedbackData(updatedHomePrefs) {
      const feedbackHomes = this.feedbackProvidedHomes
      for (let i = 0; i < feedbackHomes.length; i++) {
        const home = feedbackHomes[i]
        const feedbackWantNeedList = home.feedback.match.results
        let prefArray = []
        updatedHomePrefs.forEach(payloadPref => {
          let prefFound = false
          let prefValue = null
          for (let i = 0; i < feedbackWantNeedList.length; i++) {
            const tempValue = feedbackWantNeedList[i]
            if (
              payloadPref.text === tempValue.name &&
              payloadPref.wantNeed === tempValue.wantNeed
            ) {
              prefFound = true
              prefValue = tempValue
            }
          }
          if (prefFound) {
            prefArray.push(prefValue)
          } else {
            prefArray.push({
              match: false,
              name: payloadPref.text,
              wantNeed: payloadPref.wantNeed
            })
          }
        })

        await genericPutRequestToApi(
          UpdateFeedbackMatchUrl(this.clientData._id, home._id),
          { results: prefArray }
        )
      }
      this.profileStatus === 'agent'
        ? this.getAgentData(false)
        : this.getClientData()
      this.processUpdate = false
      this.showUpdateButton = false
    },
    closeModal() {
      if (this.showUpdateButton) {
        return this.$emit('open-confirm-modal')
      }
      return this.$emit('close-modal')
    }
  }
}
</script>

<style lang="sass" scoped>
#modalContainerCard
  padding: 16px
  min-height: 90vh
.modal-title
  color: $hwayzBrandGold
  font-size: 2rem
  font-weight: 500
.divider
  display: grid
  grid-template-columns: 1fr 1fr
  text-align: left
.chips
  color: $hwayzWhite !important
  &:hover
    cursor: pointer !important
#save__btn
  margin-top: -5%
  margin-bottom: 5%
.update-needs-wants-btn div button
  width: 100%
.preference-container
  overflow: auto
  height: 70vh
  width: 100%
  padding: 1rem 5rem 5rem
.preference-container p
  display: block

.pref-error
  border-bottom: 0.1rem $hwayzRed solid !important
  &-text
    margin-left: 5rem
    position: absolute
    font-size: 1rem
    color: $hwayzRed
    padding-top: 0.2rem

.container-input input[type="text"]
  width: 100%
  display: block
  font-size: $hwayzFont1rem4
  border-bottom: 0.1rem black solid
  margin-left: 5rem
.container-input label
  padding-left: 5rem
.area-label
  display: block
  text-transform: capitalize
  font-size: $hwayzFont1rem4
  margin-bottom: 10px !important
  margin-left: 5rem
.modal-close
  @extend %modal__button-close, %disable-selection
</style>
